import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  FolderPlus, FolderOpen, Sparkles, Users, Trash2, ChevronRight,
  ShieldCheck, ClipboardCheck, Loader2, ArrowLeft, UserPlus, X,
  FileSpreadsheet, HardDriveUpload,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREIGABE_LABEL = {
  entwurf: { text: "Entwurf", color: "bg-sand-200 text-sand-800" },
  fachliche_pruefung: { text: "Fachliche Prüfung", color: "bg-saffron text-brand-darkred" },
  freigegeben: { text: "Freigegeben", color: "bg-emerald-100 text-emerald-800" },
};
const QPR_LABEL = {
  offen: { text: "QPR offen", color: "text-sand-600" },
  in_pruefung: { text: "QPR läuft", color: "text-terra" },
  freigegeben: { text: "QPR ✓", color: "text-emerald-700" },
};

// Pflegeampel · abgeleitet aus sections.risikomatrix im Backend.
const RISK_LABEL = {
  gruen:  { text: "Risiko: grün",   dot: "bg-emerald-500", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  gelb:   { text: "Risiko: gelb",   dot: "bg-amber-400",   color: "bg-amber-50 text-amber-900 border-amber-200" },
  orange: { text: "Risiko: orange", dot: "bg-orange-500",  color: "bg-orange-50 text-orange-900 border-orange-200" },
  rot:    { text: "Risiko: rot",    dot: "bg-red-600",     color: "bg-red-50 text-red-800 border-red-200" },
};
const RISK_TOPIC_LABEL = {
  sturz: "Sturz", dekubitus: "Dekubitus", ernaehrung: "Ernährung",
  schmerz: "Schmerz", inkontinenz: "Inkontinenz", kognition: "Kognition",
};

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso || "—"; }
}

/**
 * PatientenmappeList — Startseite der Paradygma-Suite.
 * Zeigt alle Mappen der PDL/des Teams, erlaubt „Neue Mappe", „Beispiel öffnen",
 * und (für PDL) „Mitarbeiter einladen".
 */
export default function PatientenmappeList() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [team, setTeam] = useState(null);
  const [creating, setCreating] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [showMigration, setShowMigration] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lst, me] = await Promise.all([
        axios.get(`${API}/patientenmappe`, { withCredentials: true }),
        axios.get(`${API}/team/me`, { withCredentials: true }),
      ]);
      setItems(lst.data.items || []);
      setTeam(me.data);
    } catch (e) {
      toast.error("Konnte Mappen nicht laden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createDemo = async () => {
    setDemoLoading(true);
    try {
      const { data } = await axios.post(`${API}/patientenmappe/demo`, {}, { withCredentials: true });
      toast.success("Beispiel-Mappe geöffnet – Frau Musterfrau");
      nav(`/campus/app/patientenmappe/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Anlegen der Beispiel-Mappe.");
    } finally {
      setDemoLoading(false);
    }
  };

  const createEmpty = async (payload) => {
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/patientenmappe`, payload, { withCredentials: true });
      toast.success(`Mappe „${data.label}" angelegt`);
      setShowNew(false);
      nav(`/campus/app/patientenmappe/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Anlegen.");
    } finally {
      setCreating(false);
    }
  };

  const removeMappe = async (mid) => {
    if (!confirm("Diese Mappe wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/patientenmappe/${mid}`, { withCredentials: true });
      toast.success("Mappe gelöscht");
      load();
    } catch (e) { toast.error("Löschen fehlgeschlagen."); }
  };

  const canInvite = team && (team.tier === "Admin" || team.team_role === "pdl_owner" ||
    ["PDL Suite", "PDL Suite Solo", "PDL Suite Team", "PDL-Trial", "Pflege-KI Pro"].includes(team.tier));

  return (
    <div data-testid="patientenmappe-list" className="min-h-screen bg-cream-50 pb-20">
      {/* Header */}
      <div className="border-b border-cream-300 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <Link to="/campus/app/pflege-ki" className="inline-flex items-center gap-1.5 text-[13px] text-sand-600 hover:text-brand-darkred mb-3">
            <ArrowLeft className="h-3.5 w-3.5" /> Zurück zur Pflege-KI Übersicht
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center rounded-full bg-brand-darkred text-cream-50 text-[10px] font-bold tracking-widest px-2 py-0.5">PARADYGMA-SUITE</span>
                {team && <span className="text-[11px] text-sand-500">{team.tier}</span>}
              </div>
              <h1 className="font-display text-3xl sm:text-4xl text-sand-800 leading-none">Patientenmappen</h1>
              <p className="text-[14px] text-sand-600 mt-2 max-w-xl leading-relaxed">
                11-Sektionen-Pflegedokumentation nach QPR-2026-Standard. Stammdaten, SIS,
                Risikomatrix, Medikation, Maßnahmen, 360°-Beobachtung und Freigabe-Workflow —
                alles in einer Mappe.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="btn-neue-mappe"
                onClick={() => setShowNew(true)}
                className="inline-flex items-center gap-2 rounded-full bg-brand-darkred text-cream-50 px-4 py-2 text-[13.5px] font-semibold hover:bg-brand-red transition-colors">
                <FolderPlus className="h-4 w-4" /> Neue Mappe
              </button>
              <button
                data-testid="btn-beispiel-oeffnen"
                onClick={createDemo}
                disabled={demoLoading}
                className="inline-flex items-center gap-2 rounded-full bg-saffron text-brand-darkred px-4 py-2 text-[13.5px] font-semibold hover:bg-saffron-dark transition-colors disabled:opacity-60">
                {demoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Beispiel öffnen
              </button>
              {canInvite && (
                <>
                  <button data-testid="btn-mitarbeiter-liste" onClick={() => setShowMembers(true)}
                    className="inline-flex items-center gap-2 rounded-full border border-cream-300 bg-white text-sand-800 px-4 py-2 text-[13.5px] font-semibold hover:border-brand-darkred/40">
                    <Users className="h-4 w-4" /> Team
                    {team?.member_count > 0 && (
                      <span className="ml-1 rounded-full bg-brand-darkred text-cream-50 text-[10px] px-1.5">
                        {team.member_count}{team.member_limit != null ? `/${team.member_limit}` : ""}
                      </span>
                    )}
                  </button>
                </>
              )}
              <a
                href={`${API}/patientenmappe/audit/csv`}
                target="_blank" rel="noreferrer"
                data-testid="btn-audit-csv"
                className="inline-flex items-center gap-2 rounded-full border border-cream-300 bg-white text-sand-800 px-4 py-2 text-[13.5px] font-semibold hover:border-brand-darkred/40"
                title="Sammel-Audit als CSV/Excel exportieren">
                <FileSpreadsheet className="h-4 w-4" /> Audit-CSV
              </a>
              <button
                data-testid="btn-migration-open"
                onClick={() => setShowMigration(true)}
                className="inline-flex items-center gap-2 rounded-full border border-cream-300 bg-white text-sand-800 px-4 py-2 text-[13.5px] font-semibold hover:border-brand-darkred/40"
                title="Klientenakte aus LocalStorage übernehmen">
                <HardDriveUpload className="h-4 w-4" /> Migration
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {loading && (
          <div className="flex items-center justify-center py-20 text-sand-600 text-[14px]">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Mappen …
          </div>
        )}

        {!loading && items.length === 0 && (
          <div data-testid="patientenmappe-empty" className="rounded-3xl bg-white border border-cream-300 p-8 sm:p-12 text-center">
            <FolderOpen className="h-10 w-10 text-sand-400 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-display text-2xl text-sand-800 mb-2">Noch keine Patientenmappen</h3>
            <p className="text-[14px] text-sand-600 max-w-md mx-auto mb-6">
              Lege deine erste Mappe an — oder klick auf <strong className="text-brand-darkred">Beispiel öffnen</strong>,
              um in eine voll ausgefüllte Muster-Mappe (Frau Musterfrau) hineinzuschauen.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              <button onClick={() => setShowNew(true)}
                className="inline-flex items-center gap-2 rounded-full bg-brand-darkred text-cream-50 px-5 py-2.5 text-[14px] font-semibold hover:bg-brand-red">
                <FolderPlus className="h-4 w-4" /> Erste Mappe anlegen
              </button>
              <button onClick={createDemo} disabled={demoLoading}
                data-testid="btn-beispiel-oeffnen-empty"
                className="inline-flex items-center gap-2 rounded-full bg-saffron text-brand-darkred px-5 py-2.5 text-[14px] font-semibold hover:bg-saffron-dark disabled:opacity-60">
                {demoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Beispiel öffnen
              </button>
            </div>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            {items.map((it) => {
              const fg = FREIGABE_LABEL[it.freigabe_status] || FREIGABE_LABEL.entwurf;
              const qpr = QPR_LABEL[it.qpr_status] || QPR_LABEL.offen;
              const risk = RISK_LABEL[it.risk_level] || RISK_LABEL.gruen;
              const riskTitle = (it.risk_topics && it.risk_topics.length > 0)
                ? `Pflegeampel — relevante Risiken: ${it.risk_topics.map(t => RISK_TOPIC_LABEL[t] || t).join(", ")}`
                : "Pflegeampel — keine relevanten Risiken";
              return (
                <div
                  key={it.id}
                  data-testid={`mappe-card-${it.id}`}
                  data-risk-level={it.risk_level || "gruen"}
                  className="group relative rounded-2xl bg-white border border-cream-300 hover:border-brand-darkred/40 hover:shadow-hover transition-all overflow-hidden">
                  {/* Farbstreifen links · Pflegeampel */}
                  <span
                    aria-hidden="true"
                    className={`absolute left-0 top-0 bottom-0 w-1 ${risk.dot}`}
                  />
                  <button onClick={() => nav(`/campus/app/patientenmappe/${it.id}`)} className="w-full text-left p-5 pl-6 pr-14">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <div className="font-display text-xl text-sand-800 leading-tight">{it.label}</div>
                        <div className="text-[12.5px] text-sand-500 mt-0.5">
                          {it.vorname && `${it.vorname} ${it.nachname}`}
                          {it.pflegegrad != null && <> · PG {it.pflegegrad}</>}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-sand-400 group-hover:text-brand-darkred group-hover:translate-x-0.5 transition-all shrink-0 mt-1" />
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      <span
                        data-testid={`risk-badge-${it.id}`}
                        data-risk-level={it.risk_level || "gruen"}
                        title={riskTitle}
                        className={`inline-flex items-center gap-1.5 rounded-full border ${risk.color} text-[11px] font-semibold px-2 py-0.5`}>
                        <span className={`inline-block h-2 w-2 rounded-full ${risk.dot}`} />
                        {risk.text}
                        {it.risk_topics && it.risk_topics.length > 0 && (
                          <span className="opacity-70 font-normal">· {it.risk_topics.length}</span>
                        )}
                      </span>
                      <span className={`inline-flex items-center gap-1 rounded-full ${fg.color} text-[11px] font-semibold px-2 py-0.5`}>
                        <ShieldCheck className="h-3 w-3" /> {fg.text}
                      </span>
                      <span className={`inline-flex items-center gap-1 rounded-full bg-cream-100 ${qpr.color} text-[11px] font-semibold px-2 py-0.5`}>
                        <ClipboardCheck className="h-3 w-3" /> {qpr.text}
                      </span>
                    </div>
                    <div className="text-[11.5px] text-sand-500 mt-3">
                      Aktualisiert {fmtDate(it.updated_at)}
                      {it.created_by_email && <> · {it.created_by_email}</>}
                    </div>
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeMappe(it.id); }}
                    className="absolute top-3 right-3 p-1.5 rounded-full text-sand-400 hover:text-brand-darkred hover:bg-cream-100 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Mappe löschen"
                    data-testid={`btn-delete-${it.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showNew && <NewMappeModal onClose={() => setShowNew(false)} onCreate={createEmpty} loading={creating} />}
      {showMembers && <TeamPanel onClose={() => setShowMembers(false)} onInvite={() => { setShowMembers(false); setShowInvite(true); }} team={team} onChanged={load} />}
      {showInvite && <InviteModal onClose={() => setShowInvite(false)} onDone={() => { setShowInvite(false); load(); }} />}
      {showMigration && <MigrationModal onClose={() => setShowMigration(false)} onDone={() => { setShowMigration(false); load(); }} />}
    </div>
  );
}

/* ------------------------------------------------------------- Modals ---- */

function NewMappeModal({ onClose, onCreate, loading }) {
  const [form, setForm] = useState({ vorname: "", nachname: "", geburtsdatum: "", pflegegrad: "" });
  const submit = (e) => {
    e.preventDefault();
    if (!form.vorname.trim() || !form.nachname.trim()) return;
    onCreate({
      vorname: form.vorname.trim(),
      nachname: form.nachname.trim(),
      geburtsdatum: form.geburtsdatum || null,
      pflegegrad: form.pflegegrad ? Number(form.pflegegrad) : null,
    });
  };
  return (
    <Modal onClose={onClose} title="Neue Patientenmappe">
      <form onSubmit={submit} className="space-y-3">
        <FieldRow>
          <Field label="Vorname *" required>
            <input data-testid="input-vorname" required value={form.vorname} onChange={(e) => setForm({ ...form, vorname: e.target.value })} className={INPUT_CLS} />
          </Field>
          <Field label="Nachname *" required>
            <input data-testid="input-nachname" required value={form.nachname} onChange={(e) => setForm({ ...form, nachname: e.target.value })} className={INPUT_CLS} />
          </Field>
        </FieldRow>
        <FieldRow>
          <Field label="Geburtsdatum">
            <input type="date" value={form.geburtsdatum} onChange={(e) => setForm({ ...form, geburtsdatum: e.target.value })} className={INPUT_CLS} />
          </Field>
          <Field label="Pflegegrad">
            <select value={form.pflegegrad} onChange={(e) => setForm({ ...form, pflegegrad: e.target.value })} className={INPUT_CLS}>
              <option value="">—</option>
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{`PG ${n}`}</option>)}
            </select>
          </Field>
        </FieldRow>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 rounded-full border border-cream-300 py-2.5 text-[13.5px] font-semibold text-sand-700 hover:border-brand-darkred/40">
            Abbrechen
          </button>
          <button type="submit" data-testid="btn-create-mappe" disabled={loading} className="flex-1 rounded-full bg-brand-darkred text-cream-50 py-2.5 text-[13.5px] font-semibold hover:bg-brand-red disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Mappe anlegen
          </button>
        </div>
      </form>
    </Modal>
  );
}

function InviteModal({ onClose, onDone }) {
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "Pflegefachkraft" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/team/invite`, form, { withCredentials: true });
      toast.success(`${form.name} wurde eingeladen.`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Einladung fehlgeschlagen.");
    } finally { setLoading(false); }
  };
  return (
    <Modal onClose={onClose} title="Mitarbeiter einladen">
      <form onSubmit={submit} className="space-y-3">
        <p className="text-[12.5px] text-sand-600 bg-cream-100 border border-cream-300 rounded-xl p-3 leading-relaxed">
          Der/die Mitarbeiter:in kann sich <strong>sofort</strong> mit diesen Zugangsdaten einloggen
          und sieht automatisch alle deine Patientenmappen.
        </p>
        <Field label="Name *" required>
          <input data-testid="invite-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={INPUT_CLS} />
        </Field>
        <Field label="E-Mail *" required>
          <input data-testid="invite-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={INPUT_CLS} />
        </Field>
        <Field label="Rolle">
          <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={INPUT_CLS} placeholder="Pflegefachkraft" />
        </Field>
        <Field label="Temporäres Passwort * (min. 8 Zeichen)" required>
          <input data-testid="invite-password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={INPUT_CLS} />
        </Field>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 rounded-full border border-cream-300 py-2.5 text-[13.5px] font-semibold text-sand-700">Abbrechen</button>
          <button type="submit" data-testid="btn-invite-submit" disabled={loading} className="flex-1 rounded-full bg-brand-darkred text-cream-50 py-2.5 text-[13.5px] font-semibold hover:bg-brand-red disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} <UserPlus className="h-4 w-4" /> Einladen
          </button>
        </div>
      </form>
    </Modal>
  );
}

function TeamPanel({ onClose, onInvite, team, onChanged }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/team/members`, { withCredentials: true });
      setMembers(r.data.items || []);
    } catch { toast.error("Team konnte nicht geladen werden."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const remove = async (uid, name) => {
    if (!confirm(`${name} aus dem Team entfernen?`)) return;
    try {
      await axios.delete(`${API}/team/members/${uid}`, { withCredentials: true });
      toast.success("Aus Team entfernt");
      load(); onChanged?.();
    } catch { toast.error("Entfernen fehlgeschlagen."); }
  };
  return (
    <Modal onClose={onClose} title="Team-Mitglieder">
      <div className="space-y-3">
        {loading && <div className="text-center py-6 text-sand-600 text-[13px]"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Lade …</div>}
        {!loading && members.length === 0 && (
          <div className="rounded-xl bg-cream-100 border border-cream-300 p-4 text-[13px] text-sand-600 text-center">
            Noch keine Mitarbeiter:innen. Klick auf {'\u201e'}Einladen{'\u201c'}, um jemanden hinzuzufügen.
          </div>
        )}
        {!loading && members.length > 0 && (
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {members.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 rounded-xl bg-cream-50 border border-cream-300 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[13.5px] text-sand-800 truncate">{m.name}</div>
                  <div className="text-[11.5px] text-sand-500 truncate">{m.email} · {m.role || "—"}</div>
                </div>
                <button onClick={() => remove(m.id, m.name)} className="text-sand-400 hover:text-brand-darkred p-1" aria-label="Entfernen">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <button onClick={onClose} className="flex-1 rounded-full border border-cream-300 py-2.5 text-[13.5px] font-semibold text-sand-700">Schließen</button>
          <button onClick={onInvite} data-testid="btn-invite-open" className="flex-1 rounded-full bg-brand-darkred text-cream-50 py-2.5 text-[13.5px] font-semibold hover:bg-brand-red inline-flex items-center justify-center gap-2">
            <UserPlus className="h-4 w-4" /> Neu einladen
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ---- shared ---- */
const INPUT_CLS = "w-full rounded-lg border border-cream-300 bg-white px-3 py-2 text-[14px] text-sand-800 focus:outline-none focus:border-brand-darkred/60 focus:ring-2 focus:ring-brand-darkred/10";

function Modal({ onClose, title, children }) {
  return (
    <div data-testid="patientenmappe-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-espresso-deep/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-hover max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-cream-300">
          <h3 className="font-display text-xl text-sand-800">{title}</h3>
          <button onClick={onClose} className="p-1 rounded-full text-sand-400 hover:text-sand-800" aria-label="Schließen"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function FieldRow({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>; }
function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="block text-[11.5px] font-semibold uppercase tracking-wider text-sand-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

/* -------------------------------------------------------
 * MigrationModal — Liest bestehende LocalStorage-Klientenakte
 * (Key: "pki_klientenakte") und legt daraus Patientenmappen an.
 * ------------------------------------------------------- */
function MigrationModal({ onClose, onDone }) {
  const [entries, setEntries] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // pki_klientenakte layout: { [clientName]: [{type, data, timestamp}...] }
    try {
      const raw = localStorage.getItem("pki_klientenakte") || "{}";
      const parsed = JSON.parse(raw);
      const list = Object.entries(parsed).map(([name, records]) => ({
        name,
        count: Array.isArray(records) ? records.length : 0,
        records: Array.isArray(records) ? records : [],
      }));
      setEntries(list);
      const preselect = {};
      list.forEach((l) => { preselect[l.name] = true; });
      setSelected(preselect);
    } catch (e) {
      setEntries([]);
    }
  }, []);

  const toggle = (name) => setSelected((s) => ({ ...s, [name]: !s[name] }));

  const doMigrate = async () => {
    const toMigrate = entries.filter((e) => selected[e.name]);
    if (toMigrate.length === 0) {
      toast.error("Nichts ausgewählt.");
      return;
    }
    setLoading(true);
    setProgress(0);
    let ok = 0;
    let fail = 0;
    for (const [i, entry] of toMigrate.entries()) {
      try {
        // Name splitten
        const parts = entry.name.trim().split(/\s+/);
        const vorname = parts[0] || entry.name;
        const nachname = parts.slice(1).join(" ") || "—";
        const created = await axios.post(`${API}/patientenmappe`, {
          vorname, nachname, label: `${entry.name} (importiert)`,
        }, { withCredentials: true });
        const mid = created.data.id;
        // 360°-Beobachtungen aus records
        for (const rec of entry.records) {
          const text = typeof rec === "string" ? rec :
            rec.data?.text || rec.text || JSON.stringify(rec).slice(0, 500);
          await axios.post(`${API}/patientenmappe/${mid}/section/beobachtung_360/entry`, {
            datum: (rec.timestamp || new Date().toISOString()).slice(0, 10),
            autor: "Import",
            kategorie: rec.type || "Import",
            ampel: rec.ampel || "gruen",
            text,
          }, { withCredentials: true });
        }
        ok++;
      } catch (err) {
        fail++;
      }
      setProgress(Math.round(((i + 1) / toMigrate.length) * 100));
    }
    setLoading(false);
    if (ok > 0) {
      toast.success(`${ok} Mappe(n) migriert${fail ? `, ${fail} fehlgeschlagen` : ""}.`);
      // localStorage nach erfolgreicher Migration bereinigen? — Nein, sicherheitshalber lassen.
    } else {
      toast.error("Keine Mappe erstellt.");
    }
    onDone?.();
  };

  return (
    <Modal onClose={onClose} title="Migration · Klientenakte → Patientenmappe">
      <div className="space-y-3">
        <p className="text-[13px] text-sand-600 bg-cream-100 border border-cream-300 rounded-xl p-3 leading-relaxed">
          Deine bestehenden LocalStorage-Einträge werden als neue Patientenmappen
          angelegt. Vorhandene Ampel-/Aufnahmeeinträge landen in der
          <strong> 360°-Beobachtung</strong>. Die alten Daten bleiben im Browser erhalten.
        </p>

        {entries.length === 0 && (
          <div className="rounded-xl bg-cream-50 border border-cream-300 p-4 text-[13px] text-sand-600 text-center">
            Keine Legacy-Einträge in diesem Browser gefunden.
          </div>
        )}

        {entries.length > 0 && (
          <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="migration-list">
            {entries.map((e) => (
              <label key={e.name} className="flex items-center gap-3 rounded-xl bg-cream-50 border border-cream-300 px-3 py-2 cursor-pointer hover:border-brand-darkred/40">
                <input type="checkbox" className="h-4 w-4 accent-brand-darkred"
                  checked={!!selected[e.name]} onChange={() => toggle(e.name)}
                  data-testid={`migration-check-${e.name}`} />
                <div className="flex-1">
                  <div className="font-semibold text-[13.5px] text-sand-800">{e.name}</div>
                  <div className="text-[11.5px] text-sand-500">{e.count} Einträge</div>
                </div>
              </label>
            ))}
          </div>
        )}

        {loading && (
          <div className="rounded-xl bg-brand-darkred/10 border border-brand-darkred/20 p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[12.5px] font-semibold text-brand-darkred">Migriere …</span>
              <span className="text-[12.5px] text-brand-darkred">{progress} %</span>
            </div>
            <div className="h-1.5 bg-cream-200 rounded-full overflow-hidden">
              <div className="h-full bg-brand-darkred transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button onClick={onClose} className="flex-1 rounded-full border border-cream-300 py-2.5 text-[13.5px] font-semibold text-sand-700">Schließen</button>
          <button
            onClick={doMigrate}
            disabled={loading || entries.length === 0}
            data-testid="btn-migration-run"
            className="flex-1 rounded-full bg-brand-darkred text-cream-50 py-2.5 text-[13.5px] font-semibold hover:bg-brand-red disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Ausgewählte migrieren
          </button>
        </div>
      </div>
    </Modal>
  );
}
